import { lazy, Suspense, useEffect } from "react";
import AppLayout from "@/layout";
import { Route, Routes } from "react-router-dom";
import { ROUTES } from "@/lib/routes";

import PublicRoute from "./public";
import { AdminPrivateRoute } from "./guards/AdminPrivateRoute";
import PrivateRoute from "./private";

import { useUserDetails, updateAuthData, updatePermissionData } from "@/redux/function";
import { bootstrapPreview } from "@/helper/bootstrap";
import { Loader, PageNotFound, RouteErrorBoundary, ApiError, getApiErrorMessage, useAlert, useWebSocket } from "@workspace/shared";
import { API_ENDPOINTS } from "@/lib/endpoint";
import { ApiClient } from "@/lib/client";

import { ModuleName } from "@/lib/moduleNamesEnum";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Login = lazy(() => import("@/pages/auth/login"));

const ForgotPassword = lazy(() =>
  import("@/pages/auth/forgotPassword").then((m) => ({
    default: m.ForgotPassword,
  }))
);

const OtpVerification = lazy(() =>
  import("@/pages/auth/otpVerification").then((m) => ({
    default: m.OtpVerification,
  }))
);

const CheckEmailInstruction = lazy(() =>
  import("@/pages/auth/CheckEmailInstruction").then((m) => ({
    default: m.CheckEmailInstruction,
  }))
);

const ResetPassword = lazy(() =>
  import("@/pages/auth/ResetPassword").then((m) => ({
    default: m.ResetPassword,
  }))
);

const CommissionRules = lazy(() => import("@/pages/commissionRules"));
const AddEditCommissionRules = lazy(
  () => import("@/pages/commissionRules/form")
);

const RunPayout = lazy(() =>
  import("@/pages/runPayout").then((m) => ({
    default: m.RunPayout,
  }))
);

const PayoutLog = lazy(() =>
  import("@/pages/payoutLog").then((m) => ({
    default: m.PayoutLog,
  }))
);

const Target = lazy(() => import("@/pages/target"));
const AddEditTarget = lazy(() => import("@/pages/target/form"));

const SettingPage = lazy(() => import("@/pages/settings"));
const Users = lazy(() => import("@/pages/users"));
const UsersActivities = lazy(() => import("@/pages/usersActivities"));

const RolePermission = lazy(() => import("@/pages/rolePermission"));
const AddRolePermission = lazy(() => import("@/pages/rolePermission/form"));
const EditRolePermission = lazy(() => import("@/pages/rolePermission/editView"));

const Partner = lazy(() =>
  import("@/pages/partner").then((m) => ({
    default: m.Partner,
  }))
);

const AddEditPartner = lazy(() =>
  import("@/pages/partner/form").then((m) => ({
    default: m.AddEditPartner,
  }))
);

const ViewPartner = lazy(() =>
  import("@/pages/partner/view").then((m) => ({
    default: m.ViewPartner,
  }))
);

const ViewMappedUser = lazy(() =>
  import("@/pages/partner/components/mappedUser/form").then((m) => ({
    default: m.ViewMappedUser,
  }))
);

const AdminAccount = lazy(() => import("@/pages/account"));

const MessageCreditConfiguration = lazy(() =>
  import("@/pages/messageCreditConfiguration").then((m) => ({
    default: m.MessageCreditConfiguration,
  }))
);

const TenantSubscription = lazy(() =>
  import("@/pages/tenant/tenantSubscription").then((m) => ({
    default: m.TenantSubscription,
  }))
);

const Coupon = lazy(() =>
  import("@/pages/coupons/coupon").then((m) => ({
    default: m.Coupon,
  }))
);

const AddEditCoupon = lazy(() => import("@/pages/coupons/coupon/form"));

const ModelWiseConfiguration = lazy(() =>
  import("@/pages/modelWiseConfiguration").then((m) => ({
    default: m.ModelWiseConfiguration,
  }))
);

const ModelWiseConfigForm = lazy(() =>
  import("@/pages/modelWiseConfiguration/form").then((m) => ({
    default: m.ModelWiseConfigForm,
  }))
);

const EmailConfiguration = lazy(() =>
  import("@/pages/priceConfiguration/emailConfiguration").then((m) => ({
    default: m.EmailConfiguration,
  }))
);

const StorageConfiguration = lazy(() =>
  import("@/pages/priceConfiguration/storageConfiguration").then((m) => ({
    default: m.StorageConfiguration,
  }))
);

const CouponUsages = lazy(() =>
  import("@/pages/coupons/coupon-usages").then((m) => ({
    default: m.CouponUsages,
  }))
);

const TenantTransaction = lazy(() =>
  import("@/pages/tenant/tenantTransaction").then((m) => ({
    default: m.TenantTransaction,
  }))
);

const OfflineRegistration = lazy(() =>
  import("@/pages/offlineRegistration").then((m) => ({
    default: m.OfflineRegistration,
  }))
);

const AddOfflineRegistration = lazy(
  () => import("@/pages/offlineRegistration/form")
);

const AppRoutes = () => {
  const { showAlert } = useAlert();
  const userData = useUserDetails();
  const accessToken = userData?.access;
  const tenantId = userData?.tenant?.id;
  const adminId = userData?.admin?.id || userData?.user?.id;
  const idToUse = tenantId || adminId;

  const wsUrl = idToUse && accessToken ? API_ENDPOINTS.API.SYSTEM_STATUS.WEBSOCKET_URL(idToUse, accessToken) : undefined;
  const { message: permissionChangeIndicatorData } = useWebSocket(wsUrl);

  const fetchPermissions = async () => {
    if (!accessToken) return;

    try {
      ApiClient.get(API_ENDPOINTS.API.ADMIN_PERMISSION.BASE).then(
        (res: any) => {
          if (res?.data?.data) {
            updatePermissionData(res?.data?.data);
          }
          updateAuthData({
            user: { ...userData.user },
          });
        }
      );
    } catch (error) {
      const errorMessage = getApiErrorMessage(error as ApiError);
      showAlert(errorMessage, "error");
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [accessToken]);

  useEffect(() => {
    if (permissionChangeIndicatorData?.type === "admin_permission_update") {
      fetchPermissions();
    }
  }, [permissionChangeIndicatorData]);

  useEffect(() => {
    if (accessToken) {
      bootstrapPreview();
    }
  }, [accessToken]);
  return (
    <Suspense fallback={<Loader type="fullscreen" />}>
      <Routes>
        <Route element={<PublicRoute />}>
          <Route path={ROUTES.LOGIN.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Login}><Login /></RouteErrorBoundary>} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<RouteErrorBoundary moduleName={ModuleName.ForgotPassword}><ForgotPassword /></RouteErrorBoundary>} />
          <Route
            path={ROUTES.FORGOT_PASSWORD_OTP_VERIFICATION.BASE}
            element={<RouteErrorBoundary moduleName={ModuleName.OtpVerification}><OtpVerification /></RouteErrorBoundary>}
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD_OTP_VERIFICATION.SENT_EMAIL}
            element={<RouteErrorBoundary moduleName={ModuleName.CheckEmailInstruction}><CheckEmailInstruction /></RouteErrorBoundary>}
          />
          <Route
            path={ROUTES.CHANGE_PASSWORD.TOKEN(":token")}
            element={<RouteErrorBoundary moduleName={ModuleName.ResetPassword}><ResetPassword /></RouteErrorBoundary>}
          />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route path="*" element={<PageNotFound />} />
          <Route element={<AppLayout />}>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Default", permission: "Default" }} />}>
              <Route path="/" element={<RouteErrorBoundary moduleName={ModuleName.Dashboard}><Dashboard /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Default", permission: "Default" }} />}>
              <Route path={ROUTES.PROFILE.BASE} element={<RouteErrorBoundary moduleName={ModuleName.AdminAccount}><AdminAccount /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Commission Rule", permission: "View" }} />}>
              <Route path={ROUTES.COMMISSION_RULES.BASE} element={<RouteErrorBoundary moduleName={ModuleName.CommissionRules}><CommissionRules /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Commission Rule", permission: "Change" }} />}>
              <Route path={ROUTES.COMMISSION_RULES.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCommissionRules}><AddEditCommissionRules /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Commission Rule", permission: "View" }} />}>
              <Route path={`${ROUTES.COMMISSION_RULES.BASE}/:id`} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCommissionRules}><AddEditCommissionRules /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Target", permission: "View" }} />}>
              <Route path={ROUTES.TARGET.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Target}><Target /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Target", permission: "Change" }} />}>
              <Route path={ROUTES.TARGET.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditTarget}><AddEditTarget /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Target", permission: "View" }} />}>
              <Route path={`${ROUTES.TARGET.BASE}/:id`} element={<RouteErrorBoundary moduleName={ModuleName.AddEditTarget}><AddEditTarget /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Partner", permission: "View" }} />}>
              <Route path={ROUTES.PARTNER.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Partner}><Partner /></RouteErrorBoundary>} />
              <Route path={ROUTES.PARTNER.ID(":id").VIEW} element={<RouteErrorBoundary moduleName={ModuleName.ViewPartner}><ViewPartner /></RouteErrorBoundary>} />
              <Route path={ROUTES.PARTNER.VIEW_USER_BY_ID(":partnerId", ":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.ViewMappedUser}><ViewMappedUser /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Partner", permission: "Change" }} />}>
              <Route path={ROUTES.PARTNER.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditPartner}><AddEditPartner /></RouteErrorBoundary>} />
              <Route path={ROUTES.PARTNER.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditPartner}><AddEditPartner /></RouteErrorBoundary>} />
            </Route>

            <Route element={<AdminPrivateRoute permission={{ module_name: "Run Payout", permission: "View" }} />}>
              <Route path={ROUTES.RUN_PAYOUT.BASE} element={<RouteErrorBoundary moduleName={ModuleName.RunPayout}><RunPayout /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Payouts Log", permission: "View" }} />}>
              <Route path={ROUTES.PAYOUT_LOG.BASE} element={<RouteErrorBoundary moduleName={ModuleName.PayoutLog}><PayoutLog /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Default", permission: "Default" }} />}>
              <Route path={ROUTES.SETTINGS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.SettingPage}><SettingPage /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Message Credit Configuration", permission: "View" }} />}>
              <Route path={ROUTES.MESSAGE_CREDIT_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.MessageCreditConfiguration}><MessageCreditConfiguration /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Subscription", permission: "View" }} />}>
              <Route path={ROUTES.TENANT_SUBSCRIPTIONS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.TenantSubscription}><TenantSubscription /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Coupon", permission: "View" }} />}>
              <Route path={ROUTES.COUPON.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Coupon}><Coupon /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Coupon", permission: "Change" }} />}>
              <Route path={ROUTES.COUPON.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCoupon}><AddEditCoupon /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Coupon", permission: "View" }} />}>
              <Route path={`${ROUTES.COUPON.BASE}/:id`} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCoupon}><AddEditCoupon /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Model Wise Config", permission: "View" }} />}>
              <Route path={ROUTES.MODEL_WISE_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfiguration}><ModelWiseConfiguration /></RouteErrorBoundary>} />
              <Route path={ROUTES.MODEL_WISE_CONFIGURATION.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfigForm}><ModelWiseConfigForm /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Model Wise Config", permission: "Change" }} />}>
              <Route path={ROUTES.MODEL_WISE_CONFIGURATION.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfigForm}><ModelWiseConfigForm /></RouteErrorBoundary>} />
            </Route>

            <Route element={<AdminPrivateRoute permission={{ module_name: "Storage Price Configuration", permission: "View" }} />}>
              <Route path={ROUTES.STORAGE_PRICE_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.StorageConfiguration}><StorageConfiguration /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Email Credits Price Configuration", permission: "View" }} />}>
              <Route path={ROUTES.EMAIL_CREDITS_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.EmailConfiguration}><EmailConfiguration /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Coupon Usage", permission: "View" }} />}>
              <Route path={ROUTES.COUPON_USAGES.BASE} element={<RouteErrorBoundary moduleName={ModuleName.CouponUsages}><CouponUsages /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Transaction", permission: "View" }} />}>
              <Route path={ROUTES.TENANT_TRANSACTIONS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.TenantTransaction}><TenantTransaction /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Subscription", permission: "View" }} />}>
              <Route path={ROUTES.OFFLINE_REGISTRATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.OfflineRegistration}><OfflineRegistration /></RouteErrorBoundary>} />
              <Route path={ROUTES.OFFLINE_REGISTRATION.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.ViewOfflineRegistration}><AddOfflineRegistration /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Subscription", permission: "Change" }} />}>
              <Route path={ROUTES.OFFLINE_REGISTRATION.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddOfflineRegistration}><AddOfflineRegistration /></RouteErrorBoundary>} />
            </Route>

            <Route element={<AdminPrivateRoute permission={{ module_name: "User", permission: "View" }} />}>
              <Route path={ROUTES.USERS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Users}><Users /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Activity Log", permission: "View" }} />}>
              <Route path={ROUTES.USERS_ACTIVITIES.BASE} element={<RouteErrorBoundary moduleName={ModuleName.UsersActivities}><UsersActivities /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Group", permission: "View" }} />}>
              <Route path={ROUTES.ROLESANDPERMISSIONS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.RolePermission}><RolePermission /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Group", permission: "Add" }} />}>
              <Route path={ROUTES.ROLESANDPERMISSIONS.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddRolePermission}><AddRolePermission /></RouteErrorBoundary>} />
            </Route>
            <Route element={<AdminPrivateRoute permission={{ module_name: "Group", permission: "Change" }} />}>
              <Route path={ROUTES.ROLESANDPERMISSIONS.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.ViewRolePermission}><EditRolePermission /></RouteErrorBoundary>} />
            </Route>
          </Route >
        </Route >
      </Routes >
    </Suspense >
  )
};

export default AppRoutes;